import React from 'react';
import { FormattedMessage } from 'react-intl';
import { FieldValues } from 'react-hook-form';
import { GIButton, Modal } from 'app-components';
import { useDispatch, useSelector } from 'react-redux';

import {
    StyledCharacterReamining,
    StyledError,
    StyledInputContainer,
    StyledModal,
} from '../constants/ticketlist.styled';
import Dropdown from '@lib/inputs/Dropdown';
import { useFormHandler } from '../hooks/useFormHandler';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { IMarkVerifyCompleteModal } from '../constants/ticketlist.interface';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import {
    VERIFY_COMPLETE_EXTERNAL_COMMENT_COLS,
    VERIFY_COMPLETE_EXTERNAL_COMMENT_MAX_LENGTH,
    VERIFY_COMPLETE_EXTERNAL_COMMENT_ROWS,
    VERIFY_COMPLETE_INTERNAL_COMMENT_COLS,
    VERIFY_COMPLETE_INTERNAL_COMMENT_MAX_LENGTH,
    VERIFY_COMPLETE_INTERNAL_COMMENT_ROWS,
    markAsDocumentOptions,
    rejectReasonOptions,
    taskStatusTypes,
    ticketStatusTypes,
} from '../constants/ticketlist.constants';
import { useApiHandler } from '../hooks/useApiHandler';
import { ROOT_STATE } from '@store/store.types';
import { handleChange, refreshTicketList, updateTicketList } from '../constants/ticketlist.utils';
import { configs } from '@constants/configs/configs';
import { API_STATUS_CODES } from '@constants/common.constants';

export const MarkVerifyCompleteModal = ({ onClose, isAdvanceSearch }: IMarkVerifyCompleteModal) => {
    const dispatch = useDispatch();

    const {
        ticketAttachments,
        tickets: { ticketListJson },
        modalData: { taskVerifyName, activitiTaskId, processInstanceId, ticketIdVerify, taskId },
        modalFlags: { validateVerifyComments },
        listApiType,
    } = useSelector((state: ROOT_STATE) => state.ticketMgmt.ticketsList);

    const { completeUserTaskAPI, defaultListAPI, listAPI, filteredListAPI } = useApiHandler();
    const { verifyCompleteTicketForm } = useFormHandler();
    const {
        handleSubmit,
        watch,
        register,
        setValue,
        formState: { errors },
    } = verifyCompleteTicketForm;
    const values = watch();

    const onSubmit = async (formData: FieldValues) => {
        const { internalComment, externalComment, markDocumentAs, rejectReason } = formData;

        const taskObject = {
            ActivitiTaskId: activitiTaskId,
            ProcessInstanceId: processInstanceId,
            TaskId: taskId,
            TicketId: ticketIdVerify,
            ValidateComments: validateVerifyComments,
            Action: 'complete',
            formPropertiesMap: {
                taskStatus: markDocumentAs,
                taskRejectionReason: rejectReason,
            },
        };

        const payload = {
            taskObject: JSON.stringify(taskObject),
            comments: internalComment,
            commentLength: internalComment?.length,
            publicComments: externalComment,
            csrftoken: window.csrf_token,
        };
        const { statusCode } = await completeUserTaskAPI(dispatch, payload);
        if (statusCode === API_STATUS_CODES.OK) {
            updateTicketList(dispatch, ticketListJson, 'encryptedId', ticketIdVerify, {
                taskStatus: taskStatusTypes.RESOLVED,
                status: ticketStatusTypes.RESOLVED,
            });
            refreshTicketList({
                dispatch,
                listApiType,
                defaultListAPI,
                listAPI,
                filteredListAPI,
            });
        }
        onClose();
    };

    return (
        <StyledModal>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal id="verifyCompleteModal" classNames="verify-complete-modal" data-testid="verify-complete-modal">
                    <Modal.Header
                        id="verifyCompleteModalHeader"
                        titleId="ticketList.modal.verifyCompleteModal.header"
                        data-testid="verify-complete-modal-header"
                        onHide={onClose}
                    />
                    <Modal.Body data-testid="verify-complete-modal-body">
                        <ConditionalRendering isTrue={!!ticketAttachments.length}>
                            <div>
                                <h4>{taskVerifyName}</h4>
                                <ul>
                                    {ticketAttachments.map((attachment: any) => (
                                        <li
                                            key={attachment.fileName}
                                            data-testid={`verify-complete-modal-${attachment.fileName}`}
                                            style={{ listStyleType: 'none', padding: '10px 0px 0px 10px' }}
                                        >
                                            <a
                                                href={configs.endpoints.TICKETS_LIST_VIEW_ATTACHMENT_URL.processReplacements(
                                                    { documentId: attachment.encDocPath },
                                                )}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {attachment.fileName}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </ConditionalRendering>
                        <Dropdown
                            label={{
                                text: (
                                    <FormattedMessage id="ticketList.modal.verifyCompleteModal.markDocumentAsLabel" />
                                ),
                                labelClass: ['dropdown-label'],
                            }}
                            inputData={{
                                inputClass: ['dropdown-input'],
                                type: INPUT_TYPES.DROPDOWN,
                                value: values.markDocumentAs,
                                name: 'markDocumentAs',
                                isInputRequired: true,
                                options: markAsDocumentOptions,
                            }}
                            inputActions={{
                                handleFieldChange: value => handleChange('markDocumentAs', value, setValue),
                                onFieldBlur: () => {},
                            }}
                        />
                        <StyledError data-testid="mark-document-as-error">
                            {errors?.markDocumentAs?.message}
                        </StyledError>
                        <ConditionalRendering isTrue={watch('markDocumentAs') === 'rejected'}>
                            <Dropdown
                                label={{
                                    text: <FormattedMessage id="ticketList.modal.verifyCompleteModal.reasonLabel" />,
                                    labelClass: ['dropdown-label'],
                                }}
                                shouldParse
                                inputData={{
                                    inputClass: ['dropdown-input'],
                                    name: 'rejectReason',
                                    type: INPUT_TYPES.DROPDOWN,
                                    value: values.rejectReason,
                                    isInputRequired: true,
                                    options: rejectReasonOptions,
                                }}
                                inputActions={{
                                    handleFieldChange: value => handleChange('rejectReason', value, setValue),
                                    onFieldBlur: () => {},
                                }}
                            />
                            <StyledError data-testid="reject-reason-error">{errors?.rejectReason?.message}</StyledError>
                        </ConditionalRendering>
                        <StyledInputContainer>
                            <label
                                id={'verifyCompleteInternal_label'}
                                data-testid={'verifyCompleteInternal_label'}
                                className="required-true"
                                htmlFor="verifyCompleteInternal_textarea"
                            >
                                <FormattedMessage id="ticketList.modal.verifyCompleteModal.internalCommentsLabel" />
                            </label>
                            <textarea
                                id={'verifyCompleteInternal_textarea'}
                                className="verifyCompleteInternal"
                                aria-required={true}
                                data-testid={'verifyCompleteInternal_textarea'}
                                maxLength={VERIFY_COMPLETE_INTERNAL_COMMENT_MAX_LENGTH}
                                disabled={false}
                                cols={VERIFY_COMPLETE_INTERNAL_COMMENT_COLS}
                                rows={VERIFY_COMPLETE_INTERNAL_COMMENT_ROWS}
                                {...register('internalComment')}
                            />
                            <StyledCharacterReamining id={'verifyCompleteInternal_charactersLeft'}>
                                <FormattedMessage
                                    id="ticketList.characterLeft"
                                    values={{
                                        charLeft:
                                            VERIFY_COMPLETE_INTERNAL_COMMENT_MAX_LENGTH - values.internalComment.length,
                                    }}
                                />
                            </StyledCharacterReamining>
                            <StyledError>{errors?.internalComment?.message}</StyledError>
                        </StyledInputContainer>
                        <StyledInputContainer>
                            <label
                                id={'verifyCompleteExternal_label'}
                                data-testid={'verifyCompleteExternal_label'}
                                htmlFor="verifyCompleteExternal_textarea"
                            >
                                <FormattedMessage id="ticketList.modal.verifyCompleteModal.externalCommentsLabel" />
                            </label>
                            <textarea
                                id={'verifyCompleteExternal_textarea'}
                                className="verifyCompleteExternal"
                                aria-required={false}
                                data-testid={'verifyCompleteExternal_textarea'}
                                maxLength={VERIFY_COMPLETE_EXTERNAL_COMMENT_MAX_LENGTH}
                                disabled={false}
                                cols={VERIFY_COMPLETE_EXTERNAL_COMMENT_COLS}
                                rows={VERIFY_COMPLETE_EXTERNAL_COMMENT_ROWS}
                                {...register('externalComment')}
                            />
                            <StyledCharacterReamining id={'verifyCompleteExternal_charactersLeft'}>
                                <FormattedMessage
                                    id="ticketList.characterLeft"
                                    values={{
                                        charLeft:
                                            VERIFY_COMPLETE_EXTERNAL_COMMENT_MAX_LENGTH - values.externalComment.length,
                                    }}
                                />
                            </StyledCharacterReamining>
                            <StyledError data-testid="verify-complete-external-comment-error">
                                {errors?.externalComment?.message}
                            </StyledError>
                        </StyledInputContainer>
                    </Modal.Body>
                    <Modal.Controls data-testid="verify-complete-modal-controls">
                        <GIButton
                            id="verifyCompleteModalCancelButton"
                            onClickHandler={onClose}
                            variant="secondary"
                            labelId="button.cancel"
                        />
                        <GIButton
                            id="verifyCompleteModalSaveButton"
                            type="submit"
                            variant="primary"
                            labelId="ticketList.modal.verifyCompleteModal.button.taskComplete"
                        />
                    </Modal.Controls>
                </Modal>
            </form>
        </StyledModal>
    );
};
